import React from 'react';
import { TranscriptMatch } from '../services/evidenceService';

export interface HighlightedText {
  text: string;
  isHighlighted: boolean;
  matchIndex?: number;
}

export const highlightTranscriptText = (
  transcript: string, 
  matches: TranscriptMatch[]
): HighlightedText[] => {
  if (!transcript || !matches || matches.length === 0) {
    return [{ text: transcript || '', isHighlighted: false }];
  }

  const segments: HighlightedText[] = [];
  let currentIndex = 0;
  let usedPositions = new Set<number>(); // Track used positions to avoid overlaps

  // Process each match and find its position in the transcript
  const validMatches = matches
    .map((match, index) => {
      if (!match.matched_text) return null;
      
      // Try to find the match in the transcript
      let position = transcript.indexOf(match.matched_text);
      
      // If exact match not found, try case-insensitive search
      if (position === -1) {
        const lowerTranscript = transcript.toLowerCase();
        const lowerMatch = match.matched_text.toLowerCase();
        position = lowerTranscript.indexOf(lowerMatch);
        
        // If found with case-insensitive search, get the original text
        if (position !== -1) {
          const actualText = transcript.slice(position, position + match.matched_text.length);
          return { 
            ...match, 
            matched_text: actualText, 
            position, 
            originalIndex: index 
          };
        }
      }
      
      return position !== -1 ? { ...match, position, originalIndex: index } : null;
    })
    .filter((match): match is NonNullable<typeof match> => match !== null)
    .sort((a, b) => a.position - b.position);

  // Create segments with highlighting
  validMatches.forEach((match) => {
    const { matched_text, position, originalIndex } = match;
    
    // Skip if this position overlaps with a previously used position
    if (usedPositions.has(position)) return;
    
    // Add text before the match
    if (position > currentIndex) {
      segments.push({
        text: transcript.slice(currentIndex, position),
        isHighlighted: false
      });
    }

    // Add the highlighted match
    segments.push({
      text: matched_text,
      isHighlighted: true,
      matchIndex: originalIndex
    });

    // Mark this position range as used
    for (let i = position; i < position + matched_text.length; i++) {
      usedPositions.add(i);
    }

    currentIndex = position + matched_text.length;
  });

  // Add remaining text after the last match
  if (currentIndex < transcript.length) {
    segments.push({
      text: transcript.slice(currentIndex),
      isHighlighted: false
    });
  }

  return segments;
};

export const renderHighlightedText = (
  segments: HighlightedText[],
  className?: string
): React.ReactNode => {
  return segments.map((segment, index) => {
    if (segment.isHighlighted) {
      return (
        <mark
          key={index}
          className={`bg-yellow-200 px-1 rounded font-medium ${className || ''}`}
          data-match-index={segment.matchIndex}
        >
          {segment.text}
        </mark>
      );
    }
    return <span key={index}>{segment.text}</span>;
  });
}; 